import { BarChart3, CheckCircle2 } from "lucide-react";

import type {
  HouseholdIntelligenceResponse,
  HouseholdScoreContribution,
} from "@/api/householdIntelligence";
import { Card } from "@/components/ui/card";

type Props = {
  intelligence: HouseholdIntelligenceResponse;
};

export function HouseholdContributorsCard({ intelligence }: Props) {
  return (
    <Card className="p-6">
      <div className="flex items-center gap-3">
        <div className="rounded-xl bg-indigo-500/15 p-3 text-indigo-400">
          <BarChart3 size={21} />
        </div>

        <div>
          <h2 className="text-xl font-semibold text-white">
            Health Contributors
          </h2>

          <p className="text-sm text-slate-500">
            Signals contributing to the household health score
          </p>
        </div>
      </div>

      {intelligence.contributions.length === 0 ? (
        <div className="mt-5 rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 p-7 text-center">
          <CheckCircle2
            className="mx-auto text-emerald-400"
            size={26}
          />

          <p className="mt-3 font-medium text-white">
            No contributor data available
          </p>

          <p className="mt-2 text-sm leading-6 text-slate-400">
            Household health contributors will appear as attendance, care,
            and member intelligence data becomes available.
          </p>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          {intelligence.contributions.map((contribution) => (
            <ContributionRow
              key={contribution.source}
              contribution={contribution}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function ContributionRow({
  contribution,
}: {
  contribution: HouseholdScoreContribution;
}) {
  const visual = getHealthVisual(contribution.health_score);

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <p className="font-semibold text-white">
              {formatSource(contribution.source)}
            </p>

            <span className="rounded-full border border-slate-800 bg-slate-900 px-2.5 py-1 text-xs font-medium text-slate-400">
              {Math.round(contribution.weight * 100)}% weight
            </span>
          </div>

          <p className="mt-3 text-sm leading-6 text-slate-400">
            {contribution.explanation}
          </p>
        </div>

        <div className="shrink-0 text-left sm:text-right">
          <p className={`text-3xl font-bold ${visual.textClass}`}>
            {contribution.health_score}
          </p>

          <p className="text-xs text-slate-500">Health score</p>
        </div>
      </div>

      <div className="mt-5">
        <div className="h-2.5 overflow-hidden rounded-full bg-slate-800">
          <div
            className={`h-full rounded-full ${visual.barClass}`}
            style={{
              width: `${Math.max(
                0,
                Math.min(100, contribution.health_score)
              )}%`,
            }}
          />
        </div>

        <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
          <span>
            Weighted contribution
          </span>

          <span className="font-medium text-slate-300">
            {formatWeightedScore(contribution.weighted_score)}
          </span>
        </div>
      </div>
    </div>
  );
}

function getHealthVisual(score: number) {
  if (score >= 80) {
    return {
      textClass: "text-emerald-400",
      barClass: "bg-emerald-500",
    };
  }

  if (score >= 60) {
    return {
      textClass: "text-amber-400",
      barClass: "bg-amber-500",
    };
  }

  if (score >= 40) {
    return {
      textClass: "text-orange-400",
      barClass: "bg-orange-500",
    };
  }

  return {
    textClass: "text-rose-400",
    barClass: "bg-rose-500",
  };
}

function formatSource(source: string) {
  return source
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatWeightedScore(score: number) {
  return Number.isInteger(score) ? score.toString() : score.toFixed(1);
}